import { Button, Modal } from "react-bootstrap"
import { useState } from "react"
import { useGetPropertyAmenitiesQuery } from "../../../services/bClientApi"
import { useAppDispatch } from "../../../redux/store"
import { showError, showSuccess } from "../../../redux/counter/counterSlice"
import { PropertyType } from "../../../Types/propertyTypes"
import usePropertyAmenityCopy, { AmenityCopyMode, formatAmenityCopyResult } from "../../../Hooks/UseAmenities/usePropertyAmenityCopyHook"
import CopyAmenitiesOptions from "../../PropertyAmenity/CopyAmenitiesOptions/CopyAmenitiesOptions"

export default function CopyPropertyAmenitiesModal(props: {
    active: boolean
    onClose: () => void
    targetPropertyId: string
    sourceProperty: PropertyType
}) {
    const { active, onClose, targetPropertyId, sourceProperty } = props
    const dispatch = useAppDispatch()

    const [mode, setMode] = useState<AmenityCopyMode>('add-missing')
    const [selectedAmenityIds, setSelectedAmenityIds] = useState<string[]>([])
    const [copying, setCopying] = useState(false)

    const { planCopy, executePlan } = usePropertyAmenityCopy()

    const { data: sourceData, isLoading } = useGetPropertyAmenitiesQuery({
        propertyAmenitiesQuery: { propertyId: sourceProperty._id },
        pagination: { page: 1, limit: 500, all: true },
    })
    const sourceAmenities = sourceData?.docs || []

    const toggleAmenity = (amenityId: string) => {
        setSelectedAmenityIds((prev) =>
            prev.includes(amenityId) ? prev.filter((id) => id !== amenityId) : [...prev, amenityId]
        )
    }

    const handleCopy = async () => {
        if (mode === 'replace-all' && !window.confirm(
            `This deletes ALL amenities currently assigned to this property and replaces them with the ones from ${sourceProperty.title}. Continue?`
        )) {
            return
        }
        setCopying(true)
        const plan = await planCopy(sourceAmenities, targetPropertyId, mode, selectedAmenityIds)
        const result = await executePlan(plan)
        setCopying(false)

        const summary = formatAmenityCopyResult(result)
        if (result.failed > 0) {
            dispatch(showError(`Amenity copy finished with errors: ${summary}`))
        } else {
            dispatch(showSuccess(`Amenities copied: ${summary}`))
        }
        onClose()
    }

    return (
        <Modal show={active} onHide={() => { if (!copying) onClose() }}>
            <Modal.Header closeButton>
                <Modal.Title>Copy Amenities</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>
                    Copy amenities from <strong>{sourceProperty.title}</strong> to this property.
                </p>
                {isLoading ? (
                    <p className="text-muted">Loading amenities…</p>
                ) : (
                    <CopyAmenitiesOptions
                        idPrefix="copy-modal"
                        sourceAmenities={sourceAmenities}
                        selectedAmenityIds={selectedAmenityIds}
                        onToggleAmenity={toggleAmenity}
                        mode={mode}
                        onModeChange={setMode}
                    />
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant={mode === 'replace-all' ? 'danger' : 'primary'}
                    onClick={handleCopy}
                    disabled={copying || isLoading || sourceAmenities.length === 0}
                >
                    {copying ? 'Copying…' : 'Copy Amenities'}
                </Button>
                <Button variant="secondary" onClick={onClose} disabled={copying}>
                    Cancel
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
